import { Link } from 'react-router-dom'
import { cn } from '@/lib/utils'
import type { ElementType, MouseEventHandler, ReactNode } from 'react'

interface MobileFABProps {
  /** Icon component (e.g. Plus from lucide-react). */
  icon: ElementType
  /** sr-only label for accessibility. Also rendered as a tooltip on long-press if browser supports it. */
  label: string
  /** Optional small text shown next to the icon (extended FAB). */
  text?: ReactNode
  /** Navigate on tap. */
  to?: string
  /** Run a handler on tap. Mutually exclusive with `to`. */
  onClick?: MouseEventHandler<HTMLButtonElement>
  /** Override colors. Defaults to brand gradient. */
  className?: string
}

/**
 * Floating Action Button for mobile. Sits bottom-right just above the
 * mobile bottom nav (which is 4rem + safe-area). Hidden on md and up.
 *
 * Usage — drop it anywhere in a page; positioning is fixed:
 *   <MobileFAB icon={Plus} label="Add lead" to="/leads/new" />
 *   <MobileFAB icon={UserPlus} label="Invite" onClick={() => setOpen(true)} />
 */
export default function MobileFAB({
  icon: Icon,
  label,
  text,
  to,
  onClick,
  className,
}: MobileFABProps) {
  const baseClasses = cn(
    'md:hidden fixed right-4 z-30 flex items-center justify-center gap-2',
    'bg-gradient-brand text-white shadow-lg shadow-brand-royal/40',
    'rounded-full active:scale-95 transition-transform',
    'focus:outline-none focus:ring-2 focus:ring-brand-royal/50 focus:ring-offset-2',
    text ? 'h-12 px-5' : 'h-14 w-14',
    className
  )

  // Bottom nav is ~64px + safe area + a 16px gap.
  const style = { bottom: 'calc(4rem + env(safe-area-inset-bottom) + 1rem)' }

  if (to) {
    return (
      <Link
        to={to}
        aria-label={label}
        title={label}
        className={baseClasses}
        style={style}
      >
        <Icon className="h-6 w-6 flex-shrink-0" strokeWidth={2.5} />
        {text && <span className="text-sm font-semibold whitespace-nowrap">{text}</span>}
      </Link>
    )
  }

  return (
    <button
      type="button"
      onClick={onClick}
      aria-label={label}
      title={label}
      className={baseClasses}
      style={style}
    >
      <Icon className="h-6 w-6 flex-shrink-0" strokeWidth={2.5} />
      {text && <span className="text-sm font-semibold whitespace-nowrap">{text}</span>}
    </button>
  )
}
